import { Component, TemplateFunction } from '@ribajs/core'
import { handleize } from '@ribajs/utils'
import { SbCollectionComponent } from './index.js'

import type {
  ShopifyFilter,
  ShopifySortOptions,
  ShopifyTransformedSortOption,
  ShopifyTransformedSortOptions,
  ShopifyFilterValue,
} from '../types/index.js'
import type { SbDropdownComponent } from './sb-dropdown.component.js'

interface Scope {
  /** The collection handle */
  handle?: string
  /** The collection filters */
  filters?: ShopifyFilter[]
  /** Current or default collection sort option */
  sortBy?: string
  /** Optional available original collection sort options, if you do not want to offer sorting, omit this attribute */
  shopifySortOptions?: ShopifySortOptions
  /** Available (transformed) collection sort options */
  sortOptions?: ShopifyTransformedSortOptions
  /** Current active collection sort option */
  activeSortOption?: ShopifyTransformedSortOption

  // Methods

  getFilterId: SbFilterCollectionComponent['getFilterId']
  load: SbFilterCollectionComponent['load']
  /** This method is used inside the sb-dropdown template to select the sort option */
  selectSortOption: SbFilterCollectionComponent['selectSortOption']
  toggleListFilterValue: SbFilterCollectionComponent['toggleListFilterValue']

  // Elements

  sortDropdownEl?: SbDropdownComponent
  filterDropdownEls: {
    [handle: string]: SbDropdownComponent
  }
}

/**
 * A filter component to filter and sort the collection products
 * @see https://shopify.dev/themes/architecture/templates/collection#filter-collections For general information for collection filters
 * @see https://shopify.dev/themes/navigation-search/filtering/storefront-filtering For filters by product parameters
 * @see https://shopify.dev/themes/navigation-search/filtering/tag-filtering For filters by product tags
 * @see https://shopify.dev/themes/architecture/templates/collection#sort-products-in-a-collection For "sort by" and "sort options"
 *
 * TODO: Add support to filter the search result, for this we need a search component similar to the collection component
 */
export class SbFilterCollectionComponent extends Component {
  public static tagName = 'sb-filter-collection'
  protected collectionEl?: SbCollectionComponent

  static get observedAttributes(): string[] {
    return ['handle', 'filters', 'sort-by', 'shopify-sort-options']
  }

  protected requiredAttributes(): string[] {
    return ['handle', 'filters']
  }

  public loadedPages: number[] = []

  public scope: Scope = {
    handle: undefined,
    filters: undefined,
    sortBy: undefined,
    shopifySortOptions: undefined,
    activeSortOption: undefined,

    // Methods

    getFilterId: this.getFilterId.bind(this),
    load: this.load.bind(this),
    selectSortOption: this.selectSortOption.bind(this),
    toggleListFilterValue: this.toggleListFilterValue.bind(this),

    // Elements

    sortDropdownEl: undefined,
    filterDropdownEls: {},
  }

  public async load(event?: Event, updateUrl = true) {
    if (!this.collectionEl) {
      return
    }
    if (!this.scope.handle) {
      this.error(`The handle attribute is required!`)
      return
    }
    this.collectionEl.load(event, this.scope.handle, updateUrl)
  }

  public selectSortOption(
    sortOption: ShopifyTransformedSortOption,
    event?: Event,
  ) {
    event?.preventDefault()
    this.scope.activeSortOption = sortOption
    this.scope.sortDropdownEl?.hide()
    this.onSortOrFilterChanged()
  }

  public toggleListFilterValue(
    filterValue: ShopifyFilterValue,
    filter: ShopifyFilter,
    event?: Event,
  ) {
    event?.preventDefault()

    const currentActive = filterValue.active

    if (!this.filterHasMultipleSelects(filter)) {
      this.disableAllFilterValues(filter)
    }
    const dropdownEl = this.getFilterDropdownEl(filterValue)
    dropdownEl?.hide()

    this.setFilterValue(filter, filterValue, !currentActive)
    this.onSortOrFilterChanged()
  }

  /** Get an unique id for a filter, used by `getFilterDropdownEl` */
  protected getFilterId(filter: ShopifyFilter | ShopifyFilterValue) {
    return handleize(filter.param_name)
  }

  protected getFilterDropdownEl(filter: ShopifyFilter | ShopifyFilterValue) {
    return this.querySelector<SbDropdownComponent>(
      `#${this.getFilterId(filter)}`,
    )
  }

  /** Returns `true` if the filter can have multiple selects */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  protected filterHasMultipleSelects(filter: ShopifyFilter) {
    // TODO, multiple filters does not seem to work as described here https://shopify.dev/themes/navigation-search/filtering/storefront-filtering#multiple-filters
    return false
    // return filter.values?.[0]?.value !== "0" && filter.values?.[0]?.value !== "1"
  }

  protected disableAllFilterValues(filter: ShopifyFilter) {
    if (!filter.values) return
    for (const filterValue of filter.values) {
      filterValue.active = false
    }

    this.setActiveAndInactiveFilter(filter)
  }

  protected setFilterValue(
    filter: ShopifyFilter,
    filterValue: ShopifyFilterValue,
    active: boolean,
  ) {
    if (!filter.values) return
    const foundFilterValue = filter.values.find(
      curFilterValue =>
        curFilterValue.param_name === filterValue.param_name &&
        curFilterValue.value === filterValue.value,
    )
    if (foundFilterValue) foundFilterValue.active = active
    filterValue.active = active

    this.setActiveAndInactiveFilter(filter)
  }

  protected setActiveAndInactiveFilter(filter: ShopifyFilter) {
    filter.inactive_values = []
    filter.active_values = []

    if (!filter.values) return

    for (const filterValue of filter.values) {
      if (filterValue.active) {
        filter.active_values.push(filterValue)
      } else {
        filter.inactive_values.push(filterValue)
      }
    }
  }

  /**
   * Returns `true` if all filter values are inactive
   * @param filter
   * @returns
   */
  protected filterIsInactive(filter: ShopifyFilter) {
    let allInactive = true

    if (filter.type !== 'list') {
      console.warn('Currently only the filter list type is supported')
      return allInactive
    }

    if (!filter.values) {
      return allInactive
    }

    for (const filterValue of filter.values) {
      allInactive = allInactive && !filterValue.active
    }
    return allInactive
  }

  protected onSortOrFilterChanged() {
    if (!this.collectionEl) {
      throw new Error('The sb-collection element is required!')
    }

    // Sort options
    if (this.scope.activeSortOption) {
      this.collectionEl?.urlSearchParams.set(
        'sort_by',
        this.scope.activeSortOption.queryKey,
      )
    } else {
      this.collectionEl?.urlSearchParams.delete('sort_by')
    }

    // Active filters
    if (Array.isArray(this.scope.filters)) {
      for (const filter of this.scope.filters) {
        this.collectionEl?.urlSearchParams.delete(filter.param_name)
        if (filter.values) {
          for (const filterValue of filter.values) {
            if (filterValue.active) {
              this.collectionEl?.urlSearchParams.append(
                filterValue.param_name,
                filterValue.value,
              )
            }
          }
        }
      }
    }

    this.load()
  }

  protected connectedCallback() {
    super.connectedCallback()
    this.init(SbFilterCollectionComponent.observedAttributes)
  }

  protected transformSortOptions(sortOptions?: ShopifySortOptions) {
    const result: ShopifyTransformedSortOptions = []
    if (Array.isArray(sortOptions)) {
      for (const sortOption of sortOptions) {
        if (sortOption.length !== 2) {
          throw new Error(
            'A search option must be an array with to entries, the first is the query key, the second is the translated option label!',
          )
        }
        const queryKey = sortOption[0]
        const label = sortOption[1]
        result.push({
          queryKey,
          label,
        })
      }
    }
    return result
  }

  protected getSortOptionByKey(
    sortOptions: ShopifyTransformedSortOptions,
    queryKey?: string,
  ) {
    if (!queryKey) return
    return sortOptions.find(sortOption => sortOption.queryKey === queryKey)
  }

  /**
   * Called before this component bound
   */
  protected async beforeBind() {
    this.scope.sortOptions = this.transformSortOptions(
      this.scope.shopifySortOptions,
    )
    this.scope.activeSortOption = this.getSortOptionByKey(
      this.scope.sortOptions,
      this.scope.sortBy,
    )
    this.setCollectionEl()
    await super.beforeBind()
  }

  /**
   * Called after this component bound
   */
  protected async afterBind() {
    await super.afterBind()
    this.setCollectionEl()
    this.classList.add(`${SbCollectionComponent.tagName}-ready`)
  }

  /**
   * Called after all Riba components are bound
   */
  protected async afterAllBind() {
    this.setCollectionEl()
    await super.afterAllBind()
  }

  protected setCollectionEl() {
    if (!this.collectionEl) {
      this.collectionEl =
        document.querySelector(SbCollectionComponent.tagName) || undefined
    }
  }

  protected template(): ReturnType<TemplateFunction> {
    return null
  }
}
